/**
 * Tailwind CSS implementation of Rating component
 * Uses Seeds rating CSS classes from rating.css
 */

import React from "react";
import { Icon } from "@sproutsocial/seeds-react-icon";
import { Badge } from "@sproutsocial/seeds-react-badge";
import type { TypeRatingProps } from "./RatingTypes";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

/**
 * Single source of truth for the star + Badge composition, shared by both the
 * Tailwind and styled-components render paths so the mapping logic never drifts.
 */
export function renderRatingItems({
  emptyIcon = <Icon name="star-outline" color="yellow.600" />,
  halfIcon = <Icon name="star-half-outline" color="yellow.600" />,
  filledIcon = <Icon name="star-full-solid" color="yellow.600" />,
  rating,
  scale = 5,
}: TypeRatingProps): React.ReactNode {
  return (
    <>
      {Array.from({ length: scale }, (_, index) => {
        if (index < Math.floor(rating)) {
          // Full star
          return <span key={index}>{filledIcon}</span>;
        } else if (index === Math.floor(rating) && rating % 1 !== 0) {
          // Half star
          return <span key={index}>{halfIcon}</span>;
        } else {
          // Empty star
          return <span key={index}>{emptyIcon}</span>;
        }
      })}
      {
        <Badge
          size="small"
          badgeColor="neutral"
          ml={200}
        >{`${rating}/${scale}`}</Badge>
      }
    </>
  );
}

export const RatingTailwind = React.forwardRef<HTMLDivElement, TypeRatingProps>(
  ({ className, ...rest }, ref) => (
    <div className={cn("seeds-rating", className)} ref={ref}>
      {renderRatingItems(rest)}
    </div>
  )
);

RatingTailwind.displayName = "RatingTailwind";
